import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const COLORS = ["#10b981", "#f43f5e"];

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/student/analytics").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <AppShell title="Analytics">
        <p className="text-slate-500" data-testid="analytics-loading">Loading…</p>
      </AppShell>
    );
  }

  const passFail = [
    { name: "Passed", value: data.passed },
    { name: "Failed", value: data.failed },
  ];

  return (
    <AppShell title="Analytics" subtitle="Your learning trends over time.">
      <div className="grid gap-6 lg:grid-cols-3" data-testid="analytics-page">
        <div className="qs-card p-5">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Total attempts</p>
          <p className="font-display text-3xl font-black text-slate-900 mt-2" data-testid="analytics-attempts">{data.total_attempts}</p>
        </div>
        <div className="qs-card p-5">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Average score</p>
          <p className="font-display text-3xl font-black text-slate-900 mt-2" data-testid="analytics-avg">{data.average_score}%</p>
        </div>
        <div className="qs-card p-5">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Pass rate</p>
          <p className="font-display text-3xl font-black text-slate-900 mt-2">
            {data.total_attempts > 0 ? Math.round((data.passed / data.total_attempts) * 100) : 0}%
          </p>
        </div>

        <div className="qs-card p-6 lg:col-span-2">
          <h3 className="font-display text-lg font-bold text-slate-900">Score timeline</h3>
          <p className="text-sm text-slate-500">Each dot is one attempt.</p>
          <div className="h-72 mt-4">
            {data.timeline.length === 0 ? (
              <div className="h-full grid place-items-center text-slate-400 text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer>
                <LineChart data={data.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} domain={[0, 100]} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Line type="monotone" dataKey="score" stroke="#4f46e5" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="qs-card p-6">
          <h3 className="font-display text-lg font-bold text-slate-900">Pass vs Fail</h3>
          <div className="h-72 mt-4">
            {data.total_attempts === 0 ? (
              <div className="h-full grid place-items-center text-slate-400 text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={passFail} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={4}>
                    {passFail.map((entry, i) => (
                      <Cell key={`cell-${entry.name || i}`} fill={COLORS[i]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="qs-card p-6 lg:col-span-3">
          <h3 className="font-display text-lg font-bold text-slate-900">Average by subject</h3>
          <div className="h-80 mt-4">
            {data.by_subject.length === 0 ? (
              <div className="h-full grid place-items-center text-slate-400 text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={data.by_subject}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="subject" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} domain={[0, 100]} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="average" fill="#4f46e5" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
